import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { services } from '../mock';
import ServiceCard from '../components/ServiceCard';
import SEO from '../components/SEO';

const Services = () => {
  return (
    <div className="services-page">
      <SEO 
        title="Home Improvement Services | Roofing, Siding, Flooring, Decks | Fayette County, PA"
        description="Expert roofing, siding installation, deck construction, flooring & home repairs in Fayette County. Free estimates. Licensed & insured. Call (724) 420-6192 today!"
        keywords="roofing services, siding installation, deck construction, flooring installation, home repairs, Fayette County services, Mt Pleasant contractor services"
      />
      {/* Hero Section */}
      <section style={{background: 'var(--bg-page)', padding: '120px 40px 80px'}}>
        <div className="container">
          <h1 className="heading-1" style={{color: 'var(--text-primary)', marginBottom: '24px'}}>OUR SERVICES</h1>
          <p className="body-large" style={{color: 'var(--text-secondary)', maxWidth: '60ch'}}>Comprehensive home improvement solutions for every need</p>
        </div>
      </section>

      {/* Services Grid */}
      <section style={{background: 'var(--bg-card)', padding: '96px 40px'}}>
        <div className="container">
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '32px'}}>
            {services.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section style={{background: 'var(--bg-page)', padding: '96px 40px'}}>
        <div className="container" style={{maxWidth: '900px'}}>
          <div style={{marginBottom: '60px', textAlign: 'center'}}>
            <h2 className="heading-1" style={{color: 'var(--text-primary)', marginBottom: '16px'}}>SERVICE DETAILS</h2>
          </div>
          
          {/* Roofing */}
          <div style={{marginBottom: '80px'}}>
            <h3 className="heading-2" style={{color: 'var(--text-primary)', marginBottom: '24px'}}>ROOFING SERVICES</h3>
            <p className="body-medium" style={{color: 'var(--text-secondary)', marginBottom: '24px', lineHeight: '1.6'}}>
              Your roof is your home's first line of defense. We provide complete roofing solutions including replacement, repair, and maintenance. Our experienced team works with premium materials to ensure your roof protects your home for decades to come.
            </p>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px'}}>
              {['Asphalt Shingle Installation', 'Metal Roofing', 'Roof Repairs & Maintenance', 'Emergency Leak Repairs', 'Gutter Installation', 'Ventilation Systems'].map((item, i) => (
                <div key={i} style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                  <CheckCircle2 size={20} color="var(--brand-primary)" />
                  <span className="body-small" style={{color: 'var(--text-primary)'}}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Siding */}
          <div style={{marginBottom: '80px'}}>
            <h3 className="heading-2" style={{color: 'var(--text-primary)', marginBottom: '24px'}}>SIDING INSTALLATION</h3>
            <p className="body-medium" style={{color: 'var(--text-secondary)', marginBottom: '24px', lineHeight: '1.6'}}>
              Transform your home's exterior with professional siding installation. We offer a variety of materials and styles to match your aesthetic preferences and budget, all installed with precision and care.
            </p>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px'}}>
              {['Vinyl Siding', 'Fiber Cement Siding', 'Wood Siding', 'Trim & Fascia', 'Color Consultation', 'Insulation Upgrades'].map((item, i) => (
                <div key={i} style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                  <CheckCircle2 size={20} color="var(--brand-primary)" />
                  <span className="body-small" style={{color: 'var(--text-primary)'}}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Flooring */}
          <div style={{marginBottom: '80px'}}>
            <h3 className="heading-2" style={{color: 'var(--text-primary)', marginBottom: '24px'}}>FLOORING INSTALLATION</h3>
            <p className="body-medium" style={{color: 'var(--text-secondary)', marginBottom: '24px', lineHeight: '1.6'}}>
              Quality flooring transforms any space. From hardwood to tile, we install all types of flooring with expert precision. Our team ensures proper preparation, installation, and finishing for beautiful, long-lasting results.
            </p>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px'}}>
              {['Hardwood Floors', 'Laminate Flooring', 'Tile & Stone', 'Vinyl Plank', 'Floor Refinishing', 'Subfloor Repair'].map((item, i) => (
                <div key={i} style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                  <CheckCircle2 size={20} color="var(--brand-primary)" />
                  <span className="body-small" style={{color: 'var(--text-primary)'}}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* General Repairs */}
          <div style={{marginBottom: '80px'}}>
            <h3 className="heading-2" style={{color: 'var(--text-primary)', marginBottom: '24px'}}>GENERAL REPAIRS</h3>
            <p className="body-medium" style={{color: 'var(--text-secondary)', marginBottom: '24px', lineHeight: '1.6'}}>
              From small fixes to larger projects, our handyman services cover all your home repair needs. No job is too small—we're here to keep your home in excellent condition.
            </p>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px'}}>
              {['Drywall Repair', 'Interior/Exterior Painting', 'Deck Repairs & Staining', 'Door & Window Repair', 'Carpentry Work', 'Home Maintenance'].map((item, i) => (
                <div key={i} style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                  <CheckCircle2 size={20} color="var(--brand-primary)" />
                  <span className="body-small" style={{color: 'var(--text-primary)'}}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{background: 'var(--brand-primary)', padding: '80px 40px'}}>
        <div className="container" style={{textAlign: 'center'}}>
          <h2 className="heading-2" style={{color: 'var(--text-inverse)', marginBottom: '16px'}}>NEED A SERVICE?</h2>
          <p className="body-large" style={{color: 'var(--text-inverse)', marginBottom: '40px'}}>Let's discuss your project and provide a free estimate</p>
          <Link to="/contact" className="btn-secondary" style={{background: 'var(--text-inverse)', color: 'var(--brand-primary)', border: 'none'}}>
            GET FREE QUOTE
            <ArrowRight size={20} style={{marginLeft: '8px'}} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;